/**
 * V13.25 — Bannière "Fiche non revendiquée" (RGPD)
 *
 * Affichée en haut de la fiche athlète quand `origin_type` vaut
 * `imported_unclaimed` ou `legacy_review`. Fournit trois actions :
 *   1. Revendiquer cette fiche
 *   2. Signaler une erreur (sans connexion)
 *   3. M'opposer à la publication (sans connexion)
 *
 * ⚠️ Aucune donnée personnelle ne transite dans l'URL ou les query params :
 * chaque action ouvre un dialog et POSTe le formulaire.
 */
import { useState } from "react";
import { Info, ShieldAlert, UserCheck, AlertOctagon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return null;
  }
}

export function UnclaimedAthleteBanner({ athlete }) {
  const [open, setOpen] = useState(null); // 'claim' | 'signal' | 'oppose'
  const isMinor = !!athlete?.is_minor;
  const source = athlete?.source_name || null;
  const checkedAt = formatDate(athlete?.source_checked_at || athlete?.imported_at) ||
    formatDate(athlete?.imported_at);

  return (
    <>
      <div
        role="region"
        aria-label="Fiche non revendiquée"
        className="rounded-2xl border border-amber-300/70 bg-amber-50/80 dark:bg-amber-950/30 text-amber-950 dark:text-amber-100 p-4 sm:p-5 mb-6"
        data-testid="unclaimed-banner"
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 mt-0.5">
            <ShieldAlert className="h-5 w-5 text-amber-700 dark:text-amber-300" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-semibold text-base sm:text-lg leading-tight">
              Fiche non revendiquée
              {isMinor && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide bg-amber-200/70 text-amber-900 border border-amber-400/50" data-testid="unclaimed-minor-tag">
                  Mineur — accès restreint
                </span>
              )}
            </div>
            <p className="text-sm mt-1 leading-relaxed">
              Cette fiche minimale a été créée à partir de résultats publiés
              {source ? <> par <strong>{source}</strong></> : <> par une source officielle</>}
              {checkedAt ? <>, consultés le <strong>{checkedAt}</strong></> : null}.
              Elle n'a pas été créée ni enrichie par l'athlète.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => setOpen("claim")}
                className="bg-amber-700 text-white hover:bg-amber-800"
                data-testid="unclaimed-btn-claim"
              >
                <UserCheck className="h-4 w-4 mr-1.5" /> Revendiquer cette fiche
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setOpen("signal")}
                className="border-amber-500 text-amber-900 hover:bg-amber-100"
                data-testid="unclaimed-btn-signal"
              >
                <Info className="h-4 w-4 mr-1.5" /> Signaler une erreur
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setOpen("oppose")}
                className="border-red-500 text-red-800 hover:bg-red-50"
                data-testid="unclaimed-btn-oppose"
              >
                <AlertOctagon className="h-4 w-4 mr-1.5" /> M'opposer à la publication
              </Button>
            </div>
          </div>
        </div>
      </div>

      {open === "claim" && (
        <ClaimDialog athlete={athlete} onClose={() => setOpen(null)} />
      )}
      {open === "signal" && (
        <SignalDialog athlete={athlete} onClose={() => setOpen(null)} />
      )}
      {open === "oppose" && (
        <OpposeDialog athlete={athlete} onClose={() => setOpen(null)} />
      )}
    </>
  );
}

function useSubmit({ athlete, kind, buildPayload, onClose, validate }) {
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    const err = validate?.();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const payload = buildPayload();
      // Endpoint public — POST body only, aucun paramètre sensible en query.
      await api.post(`/athletes/${encodeURIComponent(athlete.slug || athlete.id)}/public/${kind}`, payload);
      toast.success("Demande enregistrée. Nous vous recontacterons rapidement.");
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Envoi impossible. Réessaie plus tard.";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };
  return { loading, submit };
}

function ClaimDialog({ athlete, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [proof, setProof] = useState("");
  const [message, setMessage] = useState("");
  const { loading, submit } = useSubmit({
    athlete,
    kind: "claim",
    buildPayload: () => ({ contact_name: name.trim(), contact_email: email.trim(), proof_url: proof.trim() || null, message: message.trim() || null }),
    onClose,
    validate: () => {
      if (!name.trim() || name.trim().length < 2) return "Ton nom complet est requis.";
      if (!email.trim() || !email.includes("@")) return "Une adresse email valide est requise.";
      return null;
    },
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="claim-dialog">
        <DialogHeader>
          <DialogTitle>Revendiquer cette fiche</DialogTitle>
          <DialogDescription>
            Explique brièvement en quoi cette fiche te concerne. Un administrateur validera ta demande.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="claim-name">Nom complet *</Label>
            <Input id="claim-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="claim-name-input" />
          </div>
          <div>
            <Label htmlFor="claim-email">Email de contact *</Label>
            <Input id="claim-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="claim-email-input" />
          </div>
          <div>
            <Label htmlFor="claim-proof">Justificatif (URL, optionnel)</Label>
            <Input id="claim-proof" placeholder="Lien réseau social / article / licence" value={proof} onChange={(e) => setProof(e.target.value)} data-testid="claim-proof-input" />
          </div>
          <div>
            <Label htmlFor="claim-msg">Message (optionnel)</Label>
            <Textarea id="claim-msg" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="claim-msg-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Annuler</Button>
          <Button onClick={submit} disabled={loading} data-testid="claim-submit">
            {loading ? "Envoi…" : "Envoyer ma demande"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignalDialog({ athlete, onClose }) {
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [errorRef, setErrorRef] = useState("");
  const { loading, submit } = useSubmit({
    athlete,
    kind: "signal",
    buildPayload: () => ({
      contact_name: name.trim() || null,
      contact_email: email.trim() || null,
      error_ref: errorRef.trim() || null,
      message: message.trim(),
    }),
    onClose,
    validate: () => {
      if (message.trim().length < 10) return "Merci de décrire l'erreur (10 caractères minimum).";
      return null;
    },
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="signal-dialog">
        <DialogHeader>
          <DialogTitle>Signaler une erreur</DialogTitle>
          <DialogDescription>
            Précise l'erreur constatée. La connexion n'est pas nécessaire — un email de contact est optionnel.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="signal-msg">Description *</Label>
            <Textarea id="signal-msg" rows={4} placeholder="Ex : l'année de la médaille de bronze est fausse…" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="signal-msg-input" />
          </div>
          <div>
            <Label htmlFor="signal-ref">Référence de l'erreur (optionnel)</Label>
            <Input id="signal-ref" placeholder="Ex : palmarès 2019 · 50m libre" value={errorRef} onChange={(e) => setErrorRef(e.target.value)} data-testid="signal-ref-input" />
          </div>
          <div>
            <Label htmlFor="signal-name">Votre nom (optionnel)</Label>
            <Input id="signal-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="signal-name-input" />
          </div>
          <div>
            <Label htmlFor="signal-email">Email de contact (optionnel)</Label>
            <Input id="signal-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signal-email-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Annuler</Button>
          <Button onClick={submit} disabled={loading} data-testid="signal-submit">
            {loading ? "Envoi…" : "Envoyer le signalement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OpposeDialog({ athlete, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const { loading, submit } = useSubmit({
    athlete,
    kind: "oppose",
    buildPayload: () => ({
      contact_name: name.trim(),
      contact_email: email.trim(),
      reason: reason.trim(),
      is_person_concerned: !!confirmed,
    }),
    onClose,
    validate: () => {
      if (!name.trim() || name.trim().length < 2) return "Ton nom complet est requis.";
      if (!email.trim() || !email.includes("@")) return "Une adresse email valide est requise.";
      if (reason.trim().length < 10) return "Merci d'indiquer le motif (10 caractères minimum).";
      if (!confirmed) return "Merci de confirmer que la demande vient de la personne concernée.";
      return null;
    },
  });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="oppose-dialog">
        <DialogHeader>
          <DialogTitle>M'opposer à la publication</DialogTitle>
          <DialogDescription>
            Ta demande sera traitée manuellement. Le contenu de la fiche restera visible jusqu'à décision.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="oppose-name">Nom complet *</Label>
            <Input id="oppose-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="oppose-name-input" />
          </div>
          <div>
            <Label htmlFor="oppose-email">Email de contact *</Label>
            <Input id="oppose-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="oppose-email-input" />
          </div>
          <div>
            <Label htmlFor="oppose-reason">Motif *</Label>
            <Textarea id="oppose-reason" rows={4} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="oppose-reason-input" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={confirmed} onCheckedChange={setConfirmed} data-testid="oppose-confirm" />
            <span>Je confirme être la personne concernée ou son représentant légal.</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Annuler</Button>
          <Button onClick={submit} disabled={loading} className="bg-red-600 text-white hover:bg-red-700" data-testid="oppose-submit">
            {loading ? "Envoi…" : "Envoyer mon opposition"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
